import React, { useState, useEffect, Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useParams, useLocation } from 'react-router-dom';
import api from '@app/services/api';
import { client as httpClient } from '@platform/http/api-client';
import '@app/ImpersonationBanner.css';
import '@app/UpdateBanner.css';
import { setDateFormat } from '@platform/app-shell/frontend/utils/dateFormat';
import { applyLookAndFeel } from '@platform/app-shell/frontend/lookAndFeel';
import { applySiteAccentFromStatus, applySiteAccentFromTheme } from '@platform/app-shell/frontend/accent';
import { AuthProvider, useAuth } from '@platform/auth/frontend/contexts/AuthContext';
import { QueryProvider } from '@platform/query';
import { TabProvider, TabPaneProvider, useTab } from '@platform/app-shell/frontend/contexts/TabContext';
import { ThemeProvider } from '@platform/app-shell/frontend/contexts/ThemeContext';
import TabBar from '@platform/app-shell/frontend/components/TabBar';
import TopNav from '@platform/app-shell/frontend/components/TopNav/TopNav';
import Home from '@platform/auth/frontend/pages/Home';
import ResetPassword from '@platform/auth-supabase/frontend/pages/ResetPassword';
import PullToRefresh from '@platform/app-shell/frontend/components/PullToRefresh';
import { HOME_URL, rememberLanding } from '@platform/app-shell/frontend/utils/landing';
import { isAdminUser } from '@platform/app-shell/frontend/utils/adminAuth';

// Lazy-loaded pages — split each into its own chunk so the initial bundle
// only contains the auth shell. Each page's deps (Monaco, Chart.js,
// ReactFlow, TipTap, etc.) load on first route hit, not on app boot.
const Structure = lazy(() => import('@modules/folders/frontend/pages/Structure'));
const Dashboards = lazy(() => import('@client/dashboards/frontend/pages/ZebraSimpleDashboards'));
const KanbanBoards = lazy(() => import('@modules/kanban/frontend/pages/KanbanBoards'));
const Settings = lazy(() => import('@platform/settings/frontend/pages/Settings'));
const Portal = lazy(() => import('@modules/support-desk/frontend/pages/Portal'));
const PortalCustomPage = lazy(() => import('@modules/support-desk/frontend/pages/Portal')
  .then((m) => ({ default: m.PortalCustomPage })));
import { inPortalGroups } from '@modules/support-desk/frontend/portal-group';
import { usePortalGroups } from '@modules/support-desk/frontend/usePortalGroups';
// The Translator (OFF unless a site admin switched it on). Imported down here, away from the
// import block at the top that client forks edit.
import { applyTranslator, setTranslatorAccount } from '@platform/translator/frontend/store';
const CreateWorkItem = lazy(() => import('@modules/database/work-items/creators/frontend/pages/CreateWorkItem'));
const CreateProject = lazy(() => import('@modules/database/work-items/creators/frontend/pages/CreateProject'));
const FieldOptionEditor = lazy(() => import('@modules/database/field-option-editors/frontend/pages/FieldOptionEditor'));
const JqlResults = lazy(() => import('@modules/zebra-search/frontend/pages/JqlResults'));
// Saved filters (Jira's Filters page): the list, and a filter's subscriptions.
const Filters = lazy(() => import('@modules/zebra-search/frontend/pages/Filters'));
const FilterSubscriptions = lazy(() => import('@modules/zebra-search/frontend/pages/FilterSubscriptions'));
const Alerts = lazy(() => import('@modules/notifications/frontend/pages/Alerts'));
// My profile: the signed-in person's own display name + picture (account menu).
const Profile = lazy(() => import('@platform/auth/frontend/pages/Profile'));
// The work-item Recycle bin: deleted items, restorable until purged. Site
// admins only, and on no menu — the recovery door for a mistake, not a
// feature people work with (kept basic, 2026-10-06).
const RecycleBin = lazy(() => import('@modules/database/work-items/frontend/pages/RecycleBin'));
// The BOM Manager and the CRM: product pages, OFF until a site's client config describes its BOM
// and its sales (config.bom / config.crm) — off, each page says so and how to turn it on.
const BomManager = lazy(() => import('@modules/bom/frontend/pages/BomManager'));
const Crm = lazy(() => import('@modules/crm/frontend/pages/Crm'));
const WorkflowViewer = lazy(() => import('@modules/database/workflows/frontend/pages/WorkflowViewer'));
const WorkItemLayoutEditor = lazy(() => import('@modules/database/screens/frontend/pages/WorkItemLayoutEditor'));
// Stride Migration is its own full-screen tool (own sidebar, own content
// area) so it lives at a top-level route instead of being embedded in
// Settings. TabBar stays visible; the Settings sidebar / chrome do not.
const StrideMigrationPage = lazy(() => import('@modules/migration/frontend/pages/StrideMigrationPage'));
// Client Customs host: bespoke pages as data + the @client/customs seam.
const CustomPage = lazy(() => import('@modules/customs/frontend/pages/CustomPage'));
// Grids & Gantts host: structures as data, one engine, /grids/:slug.
const GridPage = lazy(() => import('@modules/grids-gantts/frontend/pages/GridPage'));
// Home: the landing page — the navigation as tiles, search, recently viewed.
const HomePage = lazy(() => import('@platform/app-shell/frontend/pages/HomePage'));
// A work item's own address (/KEY, /browse/KEY): its screen across the tab.
const WorkItemFullPage = lazy(() => import('@modules/database/work-items/frontend/pages/WorkItemFullPage'));

function PageLoading() {
  return (
    <div className="loading-screen">
      <div className="spinner"></div>
      <p>Loading...</p>
    </div>
  );
}

function AuthGuard({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  // Signed in at some point in this mount → a later "no user" is a sign-out,
  // and the page it happened on is not a link to come back to.
  const hadUserRef = React.useRef(false);
  if (user) hadUserRef.current = true;

  // Once the user is authenticated, load the PWA-global date format
  // setting so every formatDate() call across the app uses the
  // admin-chosen preset. Failure is silent — we fall back to ISO.
  useEffect(() => {
    if (!user) return;
    api.getPwaGlobalSettings?.()
      .then(cfg => {
        if (cfg?.dateFormat) setDateFormat(cfg.dateFormat);
        applyLookAndFeel(cfg?.lookAndFeel); // site branding: logo, favicon, nav colours
        applyTranslator(cfg?.translator); // OFF unless an admin switched the Translator on
      })
      .catch(() => { /* silent — keep default */ });
  }, [user]);
  // each person's own language (the dictionary icon), like their theme
  useEffect(() => { setTranslatorAccount(user?.accountId || null); }, [user?.accountId]);

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  if (!user) {
    // A link followed while signed out (a work item's /KEY in a mail) is
    // taken once after the sign-in instead of being lost to Home.
    if (!hadUserRef.current) rememberLanding(location.pathname + location.search + location.hash);
    return <Navigate to="/" replace />;
  }

  return <PullToRefresh>{children}</PullToRefresh>;
}

// Persistent amber bar shown while an admin is impersonating another
// user. Sits above the tab content; "Return to your account" restores
// the admin's own session (endImpersonation) and lands on Structure.
function ImpersonationBanner() {
  const { impersonation, user, endImpersonation } = useAuth();
  const navigate = useNavigate();

  if (!impersonation) return null;

  const handleReturn = async () => {
    await endImpersonation();
    navigate(HOME_URL, { replace: true });
  };

  return (
    <div className="impersonation-banner" role="alert">
      <span className="impersonation-banner-text">
        Viewing the app as <strong>{user?.name || user?.email || 'this user'}</strong> — you are
        signed in as <strong>{impersonation.email}</strong>.
      </span>
      <button type="button" className="impersonation-banner-btn" onClick={handleReturn}>
        Return to your account
      </button>
    </div>
  );
}

// Face-pass carryovers (product glue, not archive code — DECISIONS.md):
// the page itself states what build it is, and a waiting service worker
// offers a refresh instead of silently applying next load.
// The release number is admin chrome (versioning, 2026-10-05): shown in
// admin mode only, linking to Settings → Versions & Health. /api/status
// nests it under `build` — reading the top level drew nothing at all.
function BuildFooter() {
  const { adminMode, openTab } = useTab();
  const [info, setInfo] = useState(null);
  useEffect(() => {
    if (!adminMode) return undefined;
    let on = true;
    httpClient.get('/api/status').then((r) => { if (on) setInfo(r.data?.build || null); }).catch(() => {});
    return () => { on = false; };
  }, [adminMode]);
  if (!adminMode || !info?.version) return null;
  return (
    <div style={{ padding: '4px 12px', fontSize: '11px', opacity: 0.55, textAlign: 'right' }}>
      <button type="button" onClick={() => openTab('/settings/versions', 'Versions & Health')} title="Settings → Versions & Health"
        style={{ background: 'none', border: 0, padding: 0, font: 'inherit', color: 'inherit', cursor: 'pointer' }}>
        Stride {info.version}{info.sinceRelease ? ` +${info.sinceRelease}` : ''} @ {String(info.commit || '').slice(0, 7)}
      </button>
    </div>
  );
}

// How often an open window asks the server whether a new build exists. The
// browser only checks on its own when the page NAVIGATES, and a single-page
// app never does — an installed PWA window left open on a workstation for
// days kept running the build it was opened with, while the same account on
// a freshly opened browser ran the current one. That is how "the branding
// shows on my machine but not on theirs" and "the search behaves differently
// for them" happen: two builds, one site.
const UPDATE_CHECK_MS = 30 * 60 * 1000;

function UpdateBanner() {
  const [pending, setPending] = useState(null); // { worker } | { activated: true }
  useEffect(() => {
    if (!('serviceWorker' in navigator)) return undefined;
    // IS THERE ANYTHING NEW? The worker's own signals — a worker installed,
    // or one taking over — fire on every page load that follows a deploy,
    // and since the page comes from the server first (vite.config.js) that
    // page already IS the new build: the banner said "a new version is
    // available" to someone looking at it (owner, 2026-10-15). So every
    // signal asks the one question that matters: is the build the server
    // hands out now the one this page booted? The entry script's hashed name
    // says — it changes with every build. Only a different one is news; any
    // doubt (offline, no entry found) shows the banner, as before.
    const stale = async () => {
      const mine = document.querySelector('script[type="module"][src*="/assets/index-"]')?.getAttribute('src');
      if (!mine) return true;
      try {
        const html = await (await fetch('/', { cache: 'no-store', credentials: 'same-origin' })).text();
        const theirs = /<script[^>]+src="([^"]*\/assets\/index-[^"]+\.js)"/.exec(html)?.[1];
        return !theirs || new URL(theirs, location.href).pathname !== new URL(mine, location.href).pathname;
      } catch { return true; }
    };
    const offer = (p) => { stale().then((yes) => { if (yes) setPending(p); }); };
    let reg = null;
    navigator.serviceWorker.getRegistration().then((r) => {
      if (!r) return;
      reg = r;
      if (r.waiting) offer({ worker: r.waiting });
      r.addEventListener('updatefound', () => {
        const w = r.installing;
        w?.addEventListener('statechange', () => {
          if (w.state === 'installed' && navigator.serviceWorker.controller) offer({ worker: w });
        });
      });
    }).catch(() => {});
    // The worker is built with skipWaiting, so a new build ACTIVATES without
    // waiting and main.jsx never reloads the page for it: a tab left open
    // across a deploy is told here, and reloads when the person chooses.
    const onController = () => {
      if (navigator.serviceWorker.controller) offer({ activated: true });
    };
    navigator.serviceWorker.addEventListener('controllerchange', onController);
    const check = () => { if (reg && document.visibilityState === 'visible') reg.update().catch(() => {}); };
    const timer = setInterval(check, UPDATE_CHECK_MS);
    document.addEventListener('visibilitychange', check);
    return () => {
      navigator.serviceWorker.removeEventListener('controllerchange', onController);
      clearInterval(timer);
      document.removeEventListener('visibilitychange', check);
    };
  }, []);
  if (!pending) return null;
  return (
    <div className="update-banner" role="status">
      <span className="update-banner-dot" aria-hidden="true" />
      <span className="update-banner-text">
        A new version of <strong>Zebra Stride</strong> is available.
      </span>
      <button
        type="button"
        className="update-banner-btn"
        onClick={() => { pending.worker?.postMessage({ type: 'SKIP_WAITING' }); window.location.reload(); }}
      >
        Update now
      </button>
    </div>
  );
}

// A CUSTOMER HAS NO BUSINESS IN THE APP SHELL.
//
// The guard already refuses them every surface, so the shell they would be
// shown is empty by construction: a navigation bar of things that answer
// 403, and nothing to do. That is what a new customer met after setting
// their password — the invite lands on /structure like everyone else's.
//
// So the shell sends them where they were going. Doing it HERE rather than
// in the invite mail covers every way in at once: the set-password landing,
// a bookmark, a link a colleague forwarded, a stale tab.
//
// Only a redirect. The refusal is the server's, and stays the server's.
function PortalCustomerGate({ children }) {
  const { userGroups } = useAuth();
  const location = useLocation();
  // portal-customers, plus each desk's own sign-up group (2026-10-28).
  const portalGroups = usePortalGroups(userGroups);
  // null = not yet known. Render nothing rather than flash a shell we are
  // about to leave — a customer seeing the app for half a second is exactly
  // the confusion this removes.
  if (userGroups == null || portalGroups == null) return <PageLoading />;
  // A failed group fetch reads as [] upstream. That lands an ordinary user
  // in the app, which is right: losing the redirect is a nuisance, and
  // throwing an admin at the portal would be worse.
  // A link to a custom page an admin opened to the portal (one in an email,
  // say) lands on the portal's copy of it rather than the help centre; the
  // server still decides whether it opens.
  if (inPortalGroups(userGroups, portalGroups)) {
    const m = /^\/customs\/([a-z0-9][a-z0-9-]*)\/?$/.exec(location.pathname);
    return <Navigate to={m ? `/portal/customs/${m[1]}${location.search}` : '/portal'} replace />;
  }
  return children;
}

function ProtectedLayout() {
  return (
    <AuthGuard>
      <PortalCustomerGate>
      <div className="protected-layout">
        <ImpersonationBanner />
        <UpdateBanner />
        <TabBar />
        <TopNav />
        <TabContentManager />
        <BuildFooter />
      </div>
      </PortalCustomerGate>
    </AuthGuard>
  );
}

// The customer portal renders OUTSIDE the app shell: no tab bar, no top nav,
// no navigation menu, no Stride mark. A customer is not a Stride user — they
// are someone asking their supplier for help, and every piece of chrome we
// show them is a thing they have to ignore or, worse, click.
//
// It sits beside ProtectedLayout rather than inside it, so the shell is not
// rendered and then hidden; it is never mounted at all.
function PortalLayout() {
  return (
    <AuthGuard>
      <Suspense fallback={<PageLoading />}>
        {/* RELATIVE paths. These <Routes> are nested inside the "/portal/*"
            match, so every path here resolves UNDER it — an absolute
            "/portal/project/:id" would mean "/portal/portal/project/:id",
            match nothing, and render a blank page. */}
        <Routes>
          <Route index element={<Portal />} />
          {/* ONE PORTAL (owner, 2026-10-11): a desk's old link — sent in
              emails as /portal/project/<id> — opens the help centre, which
              shows every desk the reader may raise in. */}
          <Route path="project/:projectId" element={<Navigate to="/portal" replace />} />
          <Route path="requests" element={<Portal view="requests" />} />
          <Route path="requests/:requestKey" element={<Portal />} />
          {/* A custom page an admin opened to the portal group. Before the
              two-segment form route below, so "customs/<slug>" is never read
              as a desk and a form. */}
          <Route path="customs/:slug" element={<PortalCustomPage />} />
          <Route path=":spaceId/:requestTypeId" element={<Portal />} />
          {/* Anything else under /portal goes to the help centre rather than
              to nothing — a blank page is how this bug shipped. */}
          <Route path="*" element={<Navigate to="/portal" replace />} />
        </Routes>
      </Suspense>
    </AuthGuard>
  );
}

// An admin page (Settings, the Recycle bin, the layout editor …) outside admin mode. It used to <Navigate> to
// Folders — but every open tab stays mounted, and a tab's Navigate moves the
// WHOLE app: leaving admin mode with a Settings tab open somewhere yanked
// the person off whatever page they were on (Home, a grid) and onto Folders.
// Now the tab just says so where it is, and nothing moves.
function AdminRoute({ children }) {
  const { adminMode, toggleAdminMode } = useTab();
  const { userGroups } = useAuth();
  if (adminMode) return children;
  const canAdmin = isAdminUser(userGroups);
  return (
    <div className="admin-route-off" role="status" style={{ margin: '48px auto', maxWidth: 440, textAlign: 'center', color: 'var(--text-secondary)' }}>
      <div style={{ fontSize: 28, marginBottom: 8 }} aria-hidden="true">🔒</div>
      <h2 style={{ fontSize: '1.05rem', margin: '0 0 6px', color: 'var(--text-primary)' }}>This page is for admin mode</h2>
      <p style={{ margin: '0 0 16px', fontSize: '0.9rem' }}>
        {canAdmin ? 'Admin mode is off. Turn it back on to use this page, or close this tab.' : 'Only site admins can open this page.'}
      </p>
      {canAdmin && <button type="button" className="btn-primary" onClick={toggleAdminMode}>Turn on admin mode</button>}
    </div>
  );
}

function LegacyDashboardRedirect() {
  const { id } = useParams();
  return <Navigate to={`/dashboards/${id}${window.location.hash || ''}`} replace />;
}

const TabPane = React.memo(function TabPane({ url }) {
  return (
    <Suspense fallback={<PageLoading />}>
      <Routes location={url}>
        <Route path="/structure" element={<Structure />} />
        <Route path="/structure/:id" element={<Structure />} />
        {/* Dashboards render from the product ZSD engine. Old zebra-simple-dashboards URLs
            redirect (id + hash preserved) so saved links keep working. */}
        <Route path="/dashboards" element={<Dashboards />} />
        <Route path="/dashboards/:id" element={<Dashboards />} />
        <Route path="/zebra-simple-dashboards" element={<Navigate to="/dashboards" replace />} />
        <Route path="/zebra-simple-dashboards/:id" element={<LegacyDashboardRedirect />} />
        {/* Retired pages — redirect home instead of rendering an empty pane
            (saved nav configs / restored tabs may still point here). */}
        <Route path="/kickoff" element={<Navigate to={HOME_URL} replace />} />
        <Route path="/kanban" element={<KanbanBoards />} />
        <Route path="/kanban/:id" element={<KanbanBoards />} />
        <Route path="/customs" element={<CustomPage />} />
        <Route path="/customs/:slug" element={<CustomPage />} />
        <Route path="/grids" element={<GridPage />} />
        <Route path="/grids/:slug" element={<GridPage />} />
        <Route path="/settings" element={<AdminRoute><Settings /></AdminRoute>} />
        <Route path="/settings/:category" element={<AdminRoute><Settings /></AdminRoute>} />
        <Route path="/stride-migration" element={<AdminRoute><StrideMigrationPage /></AdminRoute>} />
        <Route path="/create/:id" element={<CreateWorkItem />} />
        <Route path="/project/:id" element={<CreateProject />} />
        <Route path="/field-option-editor/:id" element={<FieldOptionEditor />} />
        <Route path="/issues" element={<JqlResults />} />
        <Route path="/filters" element={<Filters />} />
        <Route path="/filters/:id/subscriptions" element={<FilterSubscriptions />} />
        <Route path="/alerts" element={<Alerts />} />
        <Route path="/profile" element={<Profile />} />
        <Route path="/recycle-bin" element={<AdminRoute><RecycleBin /></AdminRoute>} />
        <Route path="/bom" element={<BomManager />} />
        <Route path="/bom/:key" element={<BomManager />} />
        <Route path="/crm" element={<Crm />} />
        <Route path="/crm/:tab" element={<Crm />} />
        <Route path="/workflow" element={<WorkflowViewer />} />
        <Route path="/work-item-layout/:projectId/:issueTypeId" element={<AdminRoute><WorkItemLayoutEditor /></AdminRoute>} />
        <Route path="/home" element={<HomePage />} />
        {/* A work item's own address. Static routes above outrank these, so
            a page path can never be read as a key; anything else that is
            not a key says "Page not found" instead of an empty pane. */}
        <Route path="/browse/:issueKey" element={<WorkItemFullPage />} />
        <Route path="/:issueKey" element={<WorkItemFullPage />} />
        <Route path="*" element={<WorkItemFullPage />} />
      </Routes>
    </Suspense>
  );
});

function TabContentManager() {
  const { tabs, activeTabId } = useTab();
  const [mountedIds, setMountedIds] = useState(() => new Set([activeTabId]));

  // Mount a tab the first time it becomes active — it stays mounted after that
  useEffect(() => {
    setMountedIds(prev => {
      if (prev.has(activeTabId)) return prev;
      return new Set([...prev, activeTabId]);
    });
  }, [activeTabId]);

  // Clean up mounted IDs when tabs are closed
  useEffect(() => {
    const currentIds = new Set(tabs.map(t => t.id));
    setMountedIds(prev => {
      const next = new Set([...prev].filter(id => currentIds.has(id)));
      if (next.size === prev.size) return prev;
      return next;
    });
  }, [tabs]);

  return (
    <div className="tab-content-area">
      {tabs
        .filter(tab => mountedIds.has(tab.id))
        .map(tab => {
          const isActive = tab.id === activeTabId;
          return (
            <div
              key={tab.id}
              className="tab-pane"
              style={{ display: isActive ? 'flex' : 'none' }}
            >
              {/* Every mounted tab keeps running effects even while hidden.
                  TabPaneProvider tells useSetTabTitle which tab a page belongs
                  to, so a background load can't retitle the visible tab. */}
              <TabPaneProvider tabId={tab.id}>
                <TabPane url={tab.url} />
              </TabPaneProvider>
            </div>
          );
        })}
    </div>
  );
}

function EmbeddedLayout() {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/" replace />;
  }

  return (
    <Suspense fallback={<PageLoading />}>
      <Routes>
        <Route path="/structure" element={<Structure />} />
        <Route path="/structure/:id" element={<Structure />} />
      </Routes>
    </Suspense>
  );
}

// The site accent: the client config's colour, applied on every load.
//
// It is deliberately here and not in AuthGuard — the sign-in page owns the
// first impression of the site, and it is the one screen guaranteed to be
// seen by someone whose browser has never stored anything. A personal pick
// still wins; see app-shell/frontend/accent.js for the precedence.
function useSiteAccent() {
  useEffect(() => {
    let on = true;
    httpClient.get('/api/branding/theme')
      .then((r) => { if (on) applySiteAccentFromTheme(r.data); })
      // A site whose data routes are down never mounts that route. The config
      // accent still reaches the page through the boot report, which is served
      // by the spine itself and answers even on a degraded boot.
      .catch(() => httpClient.get('/api/status')
        .then((r) => { if (on) applySiteAccentFromStatus(r.data); })
        .catch(() => { /* offline — the cached accent stands */ }));
    return () => { on = false; };
  }, []);
}

function App() {
  useSiteAccent();
  const isEmbedded = new URLSearchParams(window.location.search).get('embedded') === 'true';

  if (isEmbedded) {
    return (
      <AuthProvider>
        <QueryProvider>
          <ThemeProvider>
            <BrowserRouter>
              <TabProvider>
                <EmbeddedLayout />
              </TabProvider>
            </BrowserRouter>
          </ThemeProvider>
        </QueryProvider>
      </AuthProvider>
    );
  }

  return (
    <AuthProvider>
      <QueryProvider>
        <ThemeProvider>
          <BrowserRouter>
            <TabProvider>
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/auth/reset-password" element={<ResetPassword />} />
              <Route path="/portal/*" element={<PortalLayout />} />
              <Route path="*" element={<ProtectedLayout />} />
            </Routes>
            </TabProvider>
          </BrowserRouter>
        </ThemeProvider>
      </QueryProvider>
    </AuthProvider>
  );
}

export default App;
